import { useLazyQuery, useMutation } from '@apollo/client'
import {
  ButtonBase,
  CircularProgress,
  Menu,
  MenuItem,
  Typography
} from '@material-ui/core'
import ArrowRightIcon from '@material-ui/icons/ArrowRight'
import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import { addJob } from '../../cache/jobs'
import {
  RESERVED_FIELDS_QUERY,
  RESET_RESERVED_AVAILABILITY_MUTATION
} from '../../graphql/Inventory'
import { getTodayDate } from '../../helpers/date'
import { addCommaToPrice } from '../../helpers/numbers'
import { getTranslate } from '../../localization'
import { NumberFieldData } from '../../mockData/inventory'
import { JobsType } from '../../mockData/jobs'
import {
  MenuItemInInventory,
  ProviderDetailsForEachDate,
  ReservedField
} from '../../types/inventory'
import InventoryRowMenu from './InventoryRowMenu'
import { toReservedPooledCount } from './providerInventoryValues'
import ReserveModal from './reserveModal'
import useStyles from './styles/InventoryProviderBox'

interface InventoryProviderRowProps {
  name: string
  providerData: ProviderDetailsForEachDate[]
  availability: NumberFieldData[]
  weekendIndexes: number[]
  numberOfRatePlans: number
  numberOfRooms: number
  showRatePlans: boolean
  setshowRatePlans: React.Dispatch<React.SetStateAction<boolean>>
  loadingForRatePlans: boolean
  range: 'TWO_WEEKS' | 'MONTH'
  selectedInventoryFieldInProvider: MenuItemInInventory
  handleChangeSelectedInventoryFieldInProvider: (
    fieldType: MenuItemInInventory
  ) => void
  refreshInventory: () => void
  propertyCode: string
  roomtypeCode: string
  providerCode: string
}

const InventoryProviderRow: React.FC<InventoryProviderRowProps> = ({
  name,
  providerData,
  // availability,
  weekendIndexes,
  numberOfRatePlans,
  showRatePlans,
  setshowRatePlans,
  loadingForRatePlans,
  range,
  handleChangeSelectedInventoryFieldInProvider,
  selectedInventoryFieldInProvider,
  numberOfRooms,
  refreshInventory,
  propertyCode,
  providerCode,
  roomtypeCode
}) => {
  const classes = useStyles()
  // const [hoveredItem, sethoveredItem] = useState<number | null>(null)
  const [anchorEl, setAnchorEl] = useState<
    (EventTarget & HTMLButtonElement) | null
  >(null)
  const [clickedDate, setclickedDate] = useState<string>()
  const [isModalopen, setIsModalopen] = useState<boolean>(false)

  const menuItems: MenuItemInInventory[] = Object.keys(providerData[0])
    .filter((item) => !['date', 'reserved'].includes(item))
    .map((item) => ({ label: item, code: item, type: 'number' }))
  const [dataToRenderInRow, setdataToRenderInRow] =
    useState<NumberFieldData[]>()
  useEffect(() => {
    if (selectedInventoryFieldInProvider.code === 'availability') {
      setdataToRenderInRow(
        providerData.map((date) => ({
          date: date.date,
          value: date.availability.value,
          manual: date.availability.manual
        }))
      )
    } else if (selectedInventoryFieldInProvider.code === 'reservedAllocate') {
      setdataToRenderInRow(
        providerData.map((date) => ({
          date: date.date,
          value: date.reservedAllocate.value,
          manual: date.reservedAllocate.manual
        }))
      )
    } else if (selectedInventoryFieldInProvider.code === 'reservedPooled') {
      setdataToRenderInRow(
        providerData.map((date) => ({
          date: date.date,
          value: toReservedPooledCount(date.reservedPooled.value),
          manual: date.reservedPooled.manual
        }))
      )
    } else if (
      selectedInventoryFieldInProvider.code === 'totalAllocatedAvailability'
    ) {
      setdataToRenderInRow(
        providerData.map((date) => ({
          date: date.date,
          value: date.totalAllocatedAvailability.value,
          manual: date.totalAllocatedAvailability.manual
        }))
      )
    }
  }, [selectedInventoryFieldInProvider, range, providerData])
  const handleClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    date: string
  ) => {
    let theCurrentTarget: (EventTarget & HTMLButtonElement) | null =
      event.currentTarget
    setclickedDate(date)
    setAnchorEl(theCurrentTarget)
    theCurrentTarget = null
  }

  const handleClose = () => {
    setAnchorEl(null)
  }
  const [resetReservedAvailability] = useMutation(
    RESET_RESERVED_AVAILABILITY_MUTATION,
    {
      onCompleted: (resp) => {
        if (resp.resetReservedAvailability.trackingID) {
          const newJob: JobsType = {
            trackID: resp.resetReservedAvailability.trackingID,
            creationDate: getTodayDate(),
            status: 'InProgress'
          }
          addJob(newJob)
          toast.info(getTranslate('reset Channel inventory started'))

          refreshInventory()
        } else {
          throw new Error(getTranslate('Server error, please try again'))
        }
      },
      onError: (err) => {
        toast.error(err.message)
      }
    }
  )

  const [
    getReservedFields,
    { data: reservedFieldsData, loading: reservedFieldsLoading, refetch }
  ] = useLazyQuery<{
    reservedFields: ReservedField[]
  }>(RESERVED_FIELDS_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    onError: (err) => {
      toast.error(err.message)
    }
  })

  const onClickOnReserves = () => {
    setIsModalopen(true)
    getReservedFields({
      variables: {
        input: {
          propertyCode: propertyCode,
          date: clickedDate,
          roomTypeCode: roomtypeCode,
          providerCode: providerCode
        }
      }
    })
  }

  return (
    <div>
      <div
        className={[
          classes.roomTypeRow,
          range === 'MONTH' ? classes.roomTypeRowForMonth : ''
        ].join(' ')}>
        <div className={classes.rowNameContainer}>
          <div className={classes.roomIcon} />
          <Typography variant="h4" className={classes.rowName}>
            {getTranslate(name)}
          </Typography>{' '}
          <Typography
            variant="h6"
            className={classes.numberOfRatePlans}
            component="span"
            onClick={() => setshowRatePlans(!showRatePlans)}>
            {numberOfRatePlans} {getTranslate('RATE PLANS')}
          </Typography>
          <div className={classes.ratePlansLoading}>
            {loadingForRatePlans ? (
              <CircularProgress size={12} />
            ) : (
              <ArrowRightIcon
                onClick={() => setshowRatePlans(!showRatePlans)}
                className={[
                  classes.channelArrowRight,
                  showRatePlans ? classes.channelArrowRightCollapsed : ''
                ].join(' ')}
              />
            )}
          </div>
        </div>
        <div className={classes.dayContainer}>
          <InventoryRowMenu
            items={menuItems}
            onChangeSelectedItem={(newMenuItem) =>
              handleChangeSelectedInventoryFieldInProvider(
                newMenuItem as MenuItemInInventory
              )
            }
            activeItem={selectedInventoryFieldInProvider.label}
          />
          <ArrowRightIcon
            className={[
              classes.channelArrowRight,
              classes.channelArrowRightCollapsed
            ].join(' ')}
          />
        </div>
        {dataToRenderInRow?.map((availItem, index) => {
          return (
            <div
              key={`${name}-${availItem.date}`}
              className={[
                classes.dayContainer,
                // hoveredItem === index ? classes.selectedDayContainer : '',
                weekendIndexes.includes(index % 7)
                  ? classes.weekendDayContainer
                  : '',
                availItem.manual ? classes.editedDayContainer : ''
              ].join(' ')}>
              {/* <input
                value={availItem.value}
                onClick={(e) => {
                  sethoveredItem(index)
                  e.currentTarget.select()
                }}
                onBlur={() => {
                  sethoveredItem(null)
                }}
              /> */}
              <ButtonBase
                onClick={(e) => {
                  handleClick(e, availItem.date)
                }}>
                <Typography
                  variant="body1"
                  className={
                    range === 'MONTH' &&
                    selectedInventoryFieldInProvider.code === 'availability'
                      ? classes.availability
                      : ''
                  }>
                  {Number.isNaN(Number(availItem.value))
                    ? availItem.value
                    : addCommaToPrice(availItem.value)}
                </Typography>
              </ButtonBase>
              <span
                className={[
                  classes.numberOfRooms,
                  range === 'MONTH' && classes.numberOfRoomsMonth
                ].join(' ')}>
                {selectedInventoryFieldInProvider.code === 'availability'
                  ? ` / ${numberOfRooms}`
                  : ''}
              </span>
            </div>
          )
        })}
        <Menu
          id="simple-menu"
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleClose}>
          <MenuItem
            onClick={() => {
              resetReservedAvailability({
                variables: {
                  input: {
                    propertyCode: propertyCode,
                    providerCode: providerCode,
                    date: clickedDate,
                    roomtypeCode: roomtypeCode
                  }
                }
              })
            }}>
            {getTranslate('RRA')}
          </MenuItem>
          <MenuItem onClick={() => {}} disabled>
            {getTranslate('copy')}
          </MenuItem>
          {selectedInventoryFieldInProvider.code === 'reservedPooled' && (
            <MenuItem
              onClick={() => {
                onClickOnReserves()
              }}>
              {getTranslate('Reserves')}
            </MenuItem>
          )}
        </Menu>
      </div>
      {isModalopen && (
        <ReserveModal
          isOpen={isModalopen}
          onClose={() => {
            setIsModalopen(false)
          }}
          data={reservedFieldsData?.reservedFields || []}
          refetch={refetch}
          loading={reservedFieldsLoading}
        />
      )}
    </div>
  )
}

export default InventoryProviderRow
